import './DotChart.css';

import { useWindowResize } from '@toggle/helpers';
import { getInstanceByDom, init } from 'echarts';
import * as Types from 'echarts/types/dist/echarts';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { formatDecimalPlaces } from '~/components/table/utils';

import {
  ApacheChartBase,
  createApacheConfig,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
  Y_AXIS_LABEL_PERCENTAGE,
} from '../apache-config';
import { RemoteLegend } from '../RemoteLegend';
import {
  checkIsBubbleSizeSame,
  DotChartSeries,
  DotChartsNames,
  formatDotSeries,
  getFEDotChartProps,
  getLegendData,
} from './utils';

const AXIS_NAME_GAP = 5;
const AXIS_LABEL_PADDING = 15;

const tooltipRow = (label?: string, value?: string | number) => {
  if (!label && !value) {
    return '';
  }

  return `<p class="tooltip-row">
      <span>${label}</span>
      <span>${formatDecimalPlaces(Number(value))}</span>
    </p>`;
};

export enum DotChartEnum {
  ApacheAxisBubble = 'apache-axis-bubble',
  ApacheBubble = 'apache-bubble',
  ApacheBubbleTime = 'apache-bubble-time',
}

export interface DotChartProps extends ApacheChartBase {
  chartType: DotChartsNames;
  series: DotChartSeries;
  axisLabels?: string[];
}

export const DotChart = ({
  theme = 'dark_default',
  series,
  chartType,
  axisLabels,
  onInit,
  isModal,
}: DotChartProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  const { width, height } = useWindowResize({ targetRef: chartRef });
  const [legendInstance, setLegendInstance] = useState<Types.ECharts | null>(
    null
  );

  const isBubbleSizeSame = useMemo(() => {
    return checkIsBubbleSizeSame({
      series,
      chartType,
    });
  }, [series, chartType]);

  const zAxisInfo = useMemo(() => {
    if (!isBubbleSizeSame && axisLabels?.[2]) {
      return `Size: ${axisLabels[2]}`;
    }

    return undefined;
  }, [isBubbleSizeSame, axisLabels]);

  useEffect(() => {
    if (chartRef.current) {
      const chartDom = getInstanceByDom(chartRef.current);
      chartDom?.resize({ width, height });
    }
  }, [width, height]);

  useEffect(() => {
    const chartDom = init(chartRef.current, theme, {
      width: width || DEFAULT_CHART_WIDTH,
      height: height || DEFAULT_CHART_HEIGHT,
    });

    const chartWidth = chartRef.current?.clientWidth || DEFAULT_CHART_WIDTH;
    const chartHeight = chartRef.current?.clientHeight || DEFAULT_CHART_HEIGHT;
    const yAxisLabelWidth =
      chartWidth * Y_AXIS_LABEL_PERCENTAGE - AXIS_LABEL_PADDING;

    const formattedSeries = formatDotSeries({
      series,
      options: { chartType },
      chartWidth,
      isSizeIdentical: isBubbleSizeSame,
    });
    const baseOption = createApacheConfig({
      theme,
      isModal,
      yAxisLabelWidth,
      options: {
        xAxis: {
          scale: true,
          show: chartType !== 'apache-axis-bubble',
          name: axisLabels?.[0],
          nameMoveOverlap: true,
          nameLocation: 'middle',
          nameGap: AXIS_NAME_GAP,
          nameTruncate: {
            maxWidth: width,
            ellipsis: '...',
          },
        },
        yAxis: {
          scale: true,
          show: chartType !== 'apache-axis-bubble',
          name: axisLabels?.[1],
          nameMoveOverlap: true,
          nameLocation: 'middle',
          nameRotate: 90,
          nameGap: yAxisLabelWidth + AXIS_NAME_GAP,
          nameTruncate: {
            maxWidth: chartHeight,
            ellipsis: '...',
          },
        },
        series: chartType === 'apache-bubble-time' ? [] : formattedSeries,
        grid: {
          left: '8%',
          bottom: '10%',
        },
        legend: {
          show: false,
        },
        tooltip: {
          formatter: params => {
            const parsedParams =
              params as echarts.DefaultLabelFormatterCallbackParams;
            const dataArray = parsedParams.data as (string | number)[];

            if (!dataArray.length && !axisLabels?.length) {
              return '';
            }

            return `<div>
            <div>
              ${parsedParams.marker}
              ${parsedParams.seriesName}
            </div>
            ${dataArray
              .map((item, index) => {
                let labelIndex = index;

                if (
                  chartType === 'apache-axis-bubble' &&
                  index === dataArray.length - 1
                ) {
                  labelIndex = index + 1;
                }

                const label = axisLabels?.[labelIndex];

                if ((isBubbleSizeSame && index === 2) || !label) {
                  return '';
                }

                return `${tooltipRow(label, item)}`;
              })
              .join('')}
          </div>`;
          },
        },
        ...getFEDotChartProps({
          chartType,
          series,
          axisLabels,
          theme,
          isSizeIdentical: isBubbleSizeSame,
        }),
      },
    });

    const options =
      chartType === 'apache-bubble-time' ? (formattedSeries as any) : [];

    chartDom.setOption({ baseOption, options });

    setLegendInstance(chartDom);

    if (wrapperRef.current) {
      onInit?.(wrapperRef.current);
    }

    return () => {
      chartDom.dispose();
      setLegendInstance(null);
    };
  }, [
    isModal,
    chartType,
    theme,
    series,
    axisLabels,
    isBubbleSizeSame,
    width,
    height,
  ]);

  return (
    <div ref={wrapperRef}>
      <div ref={chartRef} />
      {legendInstance && chartType === 'apache-bubble' && (
        <RemoteLegend
          theme={theme}
          legendData={getLegendData(series).map(name => ({
            name,
            type: 'scatter',
          }))}
          targetChart={legendInstance}
          zAxisInfo={zAxisInfo}
        />
      )}
    </div>
  );
};
